import React from "react";
import { Link } from "react-router-dom";
import { Shield, ArrowLeft } from "lucide-react";

const LAST_UPDATED = "18 February 2026";

export default function TermsPage() {
  return (
    <LegalLayout title="Terms of Service" testid="terms-page">
      <p className="text-zinc-600">
        These Terms of Service (&ldquo;Terms&rdquo;) govern your access to and use of the
        Iceberg Digital marketing reporting platform (the &ldquo;Service&rdquo;).
        By creating an account or signing in, you agree to be bound by these Terms.
      </p>

      <Section title="1. Eligibility &amp; account">
        <p>
          You must be at least 18 years old and authorised to bind the entity you
          represent. Accounts are issued by an Iceberg Digital administrator;
          you are responsible for keeping your credentials confidential and for
          all activity that occurs under your account.
        </p>
      </Section>

      <Section title="2. Acceptable use">
        <ul className="list-disc pl-5 space-y-1">
          <li>You will not attempt to gain unauthorised access to other accounts, networks, or data.</li>
          <li>You will not upload material containing malicious code or otherwise interfere with the Service.</li>
          <li>You will not use the Service for any unlawful, fraudulent, or misleading purpose.</li>
          <li>You will not resell or sub-license the Service without our written consent.</li>
        </ul>
      </Section>

      <Section title="3. Client data &amp; ownership">
        <p>
          You retain all rights to the marketing data, files, and reports you
          upload or connect to the Service (&ldquo;Client Data&rdquo;). You grant Iceberg
          Digital a limited licence to process Client Data solely to provide
          and improve the Service. We do not sell Client Data to third parties.
        </p>
      </Section>

      <Section title="4. Third-party integrations">
        <p>
          The Service connects to third-party platforms (e.g. Google Ads,
          Google Analytics, Google Search Console, Google Business Profile,
          Meta Ads, LinkedIn, Microsoft Advertising, X/Twitter, Pinterest,
          OpenAI, ProductReview, Trustpilot, SendGrid, Twilio) through your
          authorised credentials. Your use of those platforms is also subject
          to their own terms; we are not responsible for changes or outages
          on third-party systems.
        </p>
      </Section>

      <Section title="5. AI-generated content">
        <p>
          Parts of the Service use large language models to draft commentary,
          recommendations, and summaries. AI output is generated automatically,
          may contain errors, and should be reviewed by a human before being
          shared with clients or acted upon. You are responsible for verifying
          accuracy before publication.
        </p>
      </Section>

      <Section title="6. Fees &amp; subscription">
        <p>
          Fees, billing cycles and included usage are set out in your separate
          commercial agreement with Iceberg Digital. We may suspend access for
          non-payment after reasonable notice.
        </p>
      </Section>

      <Section title="7. Confidentiality">
        <p>
          Each party agrees to treat the other&apos;s non-public information as
          confidential and to use it only to perform under these Terms.
        </p>
      </Section>

      <Section title="8. Service availability">
        <p>
          We aim to make the Service available 24/7 but do not guarantee
          uninterrupted availability. We may perform planned maintenance and
          will use reasonable efforts to give advance notice.
        </p>
      </Section>

      <Section title="9. Disclaimers">
        <p>
          The Service is provided on an &ldquo;as-is&rdquo; basis. To the maximum extent
          permitted by law, Iceberg Digital disclaims all warranties, express
          or implied, including merchantability, fitness for a particular
          purpose, and non-infringement.
        </p>
      </Section>

      <Section title="10. Limitation of liability">
        <p>
          To the maximum extent permitted by law, neither party will be liable
          for indirect, incidental, consequential, special or exemplary
          damages. Our aggregate liability for any claim arising out of these
          Terms will not exceed the fees paid by you in the 12 months preceding
          the claim.
        </p>
      </Section>

      <Section title="11. Termination">
        <p>
          Either party may terminate these Terms for material breach if the
          breach is not cured within 30 days of written notice. On termination
          we will provide a reasonable export window for your Client Data
          before it is deleted.
        </p>
      </Section>

      <Section title="12. Governing law">
        <p>
          These Terms are governed by the laws of New South Wales, Australia.
          The parties submit to the exclusive jurisdiction of the courts
          located in Sydney, Australia.
        </p>
      </Section>

      <Section title="13. Changes to these Terms">
        <p>
          We may update these Terms from time to time. Material changes will
          be notified through the Service or by email. Continued use after
          the effective date constitutes acceptance.
        </p>
      </Section>

      <Section title="14. Contact">
        <p>
          Questions about these Terms? Email{" "}
          <a href="mailto:legal@icebergdigital.com.au" className="text-[#002FA7] hover:underline">
            legal@icebergdigital.com.au
          </a>
          .
        </p>
      </Section>
    </LegalLayout>
  );
}

export function LegalLayout({ title, testid, children }) {
  return (
    <div className="min-h-screen bg-white" data-testid={testid}>
      <div className="max-w-3xl mx-auto px-6 py-12 sm:py-16">
        <Link to="/login"
          className="inline-flex items-center gap-1 text-sm text-[#002FA7] hover:underline mb-8"
          data-testid="legal-back">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to sign in
        </Link>

        <div className="flex items-center gap-3 mb-3">
          <div className="h-10 w-10 rounded-sm bg-[#002FA7] text-white grid place-items-center">
            <Shield className="h-5 w-5" />
          </div>
          <div className="label-overline">Iceberg Digital · Legal</div>
        </div>
        <h1 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tight">
          {title}
        </h1>
        <p className="text-[12px] font-mono text-zinc-500 mt-2">
          Last updated: {LAST_UPDATED}
        </p>

        <div className="mt-10 space-y-8 text-zinc-700 leading-relaxed">
          {children}
        </div>

        <div className="mt-16 pt-8 border-t border-zinc-100 text-[12px] text-zinc-500 flex gap-4 flex-wrap">
          <Link to="/terms" className="hover:text-[#002FA7]">Terms of Service</Link>
          <Link to="/privacy" className="hover:text-[#002FA7]">Privacy Policy</Link>
          <Link to="/login" className="hover:text-[#002FA7]">Sign in</Link>
          <span className="ml-auto">&copy; {new Date().getFullYear()} Iceberg Digital</span>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-display font-bold text-lg text-zinc-900 mb-2">{title}</h2>
      <div className="text-sm">{children}</div>
    </section>
  );
}
